import { useSelector } from 'react-redux';
import { createAction, createReducer } from 'redux-act';

import type { ILocationV2 } from '@sravni/types/lib/locations';

import type { IMicrocreditRegion } from '@src/@types/clienttypes';
import { Currency } from '@src/@types/currency';
import type * as Microcredits from '@src/@types/microcredits';
import { Experiments } from '@src/constants/abTest';
import { FILTERS_DICTIONARIES_KEYS } from '@src/constants/dictionaries';
import { DEFAULT_FILTERS, DEFAULT_REGION_ROUTE, MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { getOffersWithAdvertising } from '@src/helpers/advertising';
import { getBenefitsNamesByValues } from '@src/helpers/dictionaries';
import { loadGuruleadsList } from '@src/services/guruleads';
import { loadLeadsSuList } from '@src/services/leadssu';
import { loadMicroCreditsRegions } from '@src/services/locations';
import {
  loadCalculatorList,
  loadListByIds,
  loadLandingList,
  loadList,
  loadPopupList,
} from '@src/services/microcredits';
import type { ReduxAction } from '@src/store/types';

import { getDictionaryFieldsByAlias } from './dictionaries';

import type { IGlobalState } from '.';

const loadByKey = {
  [ListKeys.LIST]: loadList,
  [ListKeys.PARTNERS]: loadList,
  [ListKeys.LANDING]: loadList,
  [ListKeys.BRANDING]: loadLandingList,
  [ListKeys.LEADSSU]: loadLeadsSuList,
  [ListKeys.GURULEADS]: loadGuruleadsList,
  [ListKeys.PROMOTION]: loadList,
  [ListKeys.ONLINE]: loadList,
  [ListKeys.CALCULATOR]: loadCalculatorList,
  [ListKeys.BESTPRODUCTS]: loadList,
  [ListKeys.BESTOFFERS]: loadPopupList,
  [ListKeys.NOVINKI]: loadList,
  [ListKeys.MOBILE_POPUP]: loadPopupList,
  [ListKeys.WL_PAGE]: loadList,
  [ListKeys.WL_DENIED_PAGE]: loadList,
  [ListKeys.SIDEBAR_BANNER]: loadList,
  [ListKeys.SPEC_OFFERS]: loadList,
  [ListKeys.SPEC_OFFERS_BY_IDS]: loadListByIds,
  [ListKeys.ZAIMY_POD_PTS]: loadList,
  [ListKeys.KAZAKHSTAN]: loadList,
  [ListKeys.NEW_SPECIAL]: loadList,
  [ListKeys.CRM]: loadList,
  [ListKeys.MFO_PARTNER_2GIS]: loadList,
  [ListKeys.FULL_DEAL_AND_PING3_OFFERS]: loadList,
};

const DEFAULT_LIST_KEY = ListKeys.LIST;

interface IState {
  lists?: Record<ListKeys.LIST | string, Microcredits.List.IStoreData>;
  isExtraFiltersVisible: boolean;
  filters?: Microcredits.IFilters;
  regions: {
    items: IMicrocreditRegion[];
  };
  metadata?: Microcredits.List.IMetadata;
}

const initialState: IState = {
  lists: {},
  isExtraFiltersVisible: false,
  filters: {
    location: '6.83.',
    currency: Currency.RUB,
    limit: 20,
  },
  regions: {
    items: [],
  },
};

const setMicroCreditsList = createAction<{ key: string; data: Partial<Microcredits.List.IStoreData> }>(
  'credits/list/set',
);
export const setMicroCredits = createAction<{
  lists: Record<string, Microcredits.List.IStoreData>;
  metadata?: Microcredits.List.IMetadata;
}>('credits/all/set');

const setMicroCreditsMetadata = createAction<IState['metadata']>('credits/metadata/set');

const setRegions = createAction<IState['regions']>('credits/regions/set');

export const microcreditsReducer = createReducer<IState>({}, initialState)
  .on(setMicroCreditsMetadata, (state, payload) => ({ ...state, metadata: payload }))
  .on(setRegions, (state, payload) => ({ ...state, regions: payload }))
  .on(setMicroCreditsList, (state, payload) => {
    const { key, data } = payload;
    const { lists } = state;

    if (!lists) {
      return {
        ...state,
        lists: { [key]: data },
      };
    }

    return {
      ...state,
      lists: { ...lists, [key]: { ...lists[key], ...data } },
    };
  })
  .on(setMicroCredits, (state, { lists, metadata }) => ({
    ...state,
    lists,
    metadata,
  }));

interface IFetchOfferParams {
  key: ListKeys;
  query?: Microcredits.List.IReq;
  params?: Microcredits.List.IReq;
  preloadedLimit?: number;

  formatOffers?: (items: Microcredits.List.IMicrocredit[]) => Microcredits.List.IMicrocredit[];
}

const adjustLoadingKey = (sourceKey: ListKeys, params: Microcredits.List.IReq): ListKeys =>
  [ListKeys.SPEC_OFFERS, ListKeys.CRM].includes(sourceKey) && params.ids ? ListKeys.SPEC_OFFERS_BY_IDS : sourceKey;

export function fetchOffers({
  key,
  query = {},
  params = {},
  preloadedLimit,
  formatOffers,
}: IFetchOfferParams): ReduxAction {
  return async (dispatch, getState) => {
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let benefits;
    dispatch(
      setMicroCreditsList({
        key,
        data: {
          items: [],
          count: 0,
          query,
          params,
          loading: true,
          error: false,
          loadingMore: false,
        },
      }),
    );

    if (query.benefits) {
      const benefitsFields =
        getDictionaryFieldsByAlias(getState().dictionaries, FILTERS_DICTIONARIES_KEYS.BENEFITS) || [];
      benefits = getBenefitsNamesByValues(benefitsFields, query.benefits);
    }
    const loadingKey = adjustLoadingKey(key, params);

    try {
      const request = loadByKey[loadingKey]({
        ...query,
        ...params,
        limit: preloadedLimit ?? query.limit,
        benefits,
        withBonus: key === ListKeys.PROMOTION || undefined,
      }) as Promise<Microcredits.List.IRes>;

      const { items: rawItems, total: rawTotal, count: rawCount, metadata } = await request;

      const { items } = getOffersWithAdvertising(rawItems);
      const offers = formatOffers ? formatOffers(items) : items;

      const total = items.length && rawTotal;
      const count = formatOffers ? offers.length : offers.length && rawCount;

      dispatch(
        setMicroCreditsList({
          key,
          data: {
            items: offers,
            total,
            count,
            query,
            params,
            loading: false,
            error: false,
          },
        }),
      );

      if (metadata) {
        metadata.counts.offers = count;
        dispatch(setMicroCreditsMetadata(metadata));
      }
    } catch (error) {
      console.error('FETCH MICROCREDITS ERROR: ', error.message);
      dispatch(setMicroCreditsList({ key, data: { error: true, loading: false } }));
    }
  };
}

export const getCreditsLists = (state: IGlobalState) => state.credits.lists || {};
const getCreditsList = (state: IGlobalState, key: ListKeys = DEFAULT_LIST_KEY) => {
  if (!state.credits.lists) return {};

  return state.credits.lists[key] ?? {};
};

type FetchMoreOffersParams = {
  key: ListKeys;
  init?: boolean;
  features?: { isSeaExcludeFeatureExperiment: boolean };
};
export function fetchMoreOffersAB({
  key = DEFAULT_LIST_KEY,
  init = false,
  features,
}: FetchMoreOffersParams): ReduxAction {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      total: internalTotal = 0,
      items: loadedItems,
      loading,
      loadingMore,
      query,
      params,
    } = getCreditsList(state, key);

    if (Array.isArray(loadedItems) && loadedItems.length < internalTotal && !loading && !loadingMore) {
      dispatch(
        setMicroCreditsList({
          key,
          data: {
            loadingMore: true,
            errorLoadingMore: false,
          },
        }),
      );

      // eslint-disable-next-line @typescript-eslint/init-declarations
      let benefits;

      if (query.benefits) {
        const benefitsFields =
          getDictionaryFieldsByAlias(getState().dictionaries, FILTERS_DICTIONARIES_KEYS.BENEFITS) || [];
        benefits = getBenefitsNamesByValues(benefitsFields, query.benefits);
      }

      try {
        const request = loadByKey[key]({
          ...query,
          ...params,
          ...(init && query?.limit && { limit: query.limit - loadedItems.length }),
          ...(features?.isSeaExcludeFeatureExperiment ? { experiment: Experiments.EXCLUDE_FULLDEAL_OFFERS } : {}),
          skip: loadedItems.length,
          withBonus: key === ListKeys.PROMOTION || undefined,
          benefits,
        }) as Promise<Microcredits.List.IRes>;

        const { items: rawItems, total, count: rawCount } = await request;

        const { items } = getOffersWithAdvertising([...loadedItems, ...rawItems]);

        dispatch(
          setMicroCreditsList({
            key,
            data: {
              items,
              total,
              count: rawCount,
              query,
              params,
              loadingMore: false,
              errorLoadingMore: false,
            },
          }),
        );
      } catch (error) {
        console.error('FETCH MORE MICROCREDITS ERROR: ', error.message);
        dispatch(setMicroCreditsList({ key, data: { errorLoadingMore: true, loadingMore: false } }));
      }
    }
  };
}

type FetchBestOffers = {
  location: ILocationV2;
};

export function fetchBestOffers({ location }: FetchBestOffers): ReduxAction {
  return fetchOffers({
    key: ListKeys.BESTPRODUCTS,
    query: DEFAULT_FILTERS[MODES.BESTPRODUCTS],
    params: {
      location: location?.route || DEFAULT_REGION_ROUTE,
    },
  });
}

export const fetchMicroCreditsRegions =
  (seoActiveCitiesUrl?: string): ReduxAction =>
  async (dispatch) => {
    try {
      const { items } = await loadMicroCreditsRegions({ adsOnly: true, seoActiveCitiesUrl });
      dispatch(setRegions({ items }));
    } catch (error) {
      dispatch(setRegions({ items: [] }));
    }
  };

// LIST GETTERS
export const getCreditsMetadata = (state: IGlobalState) => state.credits.metadata;

const DEFAULT_LIST_QUERY = {};
const getCreditsFilters = (state: IGlobalState, key: ListKeys) => {
  const list = getCreditsList(state, key);

  return list.query || DEFAULT_LIST_QUERY;
};
const getMicroCreditsRegions = (state: IGlobalState) => state.credits.regions;
// LIST SELECTORS
export function useSelectCreditsList(key: ListKeys) {
  return useSelector((state: IGlobalState) => getCreditsList(state, key));
}
export function useSelectCreditsFilters(key: ListKeys): IState['filters'] {
  return useSelector((state: IGlobalState) => getCreditsFilters(state, key));
}
export function useSelectMicroCreditsRegions(): IState['regions'] {
  return useSelector<IGlobalState, IState['regions']>(getMicroCreditsRegions);
}
export function useSelectMicroCreditsMetadata() {
  return useSelector(getCreditsMetadata);
}
